import React, { useEffect, useState } from "react";
import { Search, Smartphone, ArrowLeft, ChevronRight, Loader2, Layers } from "lucide-react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../services/api";

interface Device {
  id: number;
  name: string;
  image: string | null;
  category_id: number | null;
}

interface Category {
  id: number;
  name: string;
}

const SelectProduct: React.FC = () => {
  const { brandId } = useParams<{ brandId: string }>();
  const [searchParams] = useSearchParams();
  const brandName = searchParams.get("brand") || "Device";
  const navigate = useNavigate();

  // Core Data States
  const [models, setModels] = useState<Device[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [filteredModels, setFilteredModels] = useState<Device[]>([]);
  
  // Filter States
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);

  // Fetch updated API payload structure
  useEffect(() => {
    const fetchModelsAndCategories = async () => {
      if (!brandId) return;
      setLoading(true);
      try {
        const data = await api.get(`/devices/by-brand/${brandId}`);
        
        // Unpack the new API structure safely
        const fetchedDevices = data?.devices || [];
        const fetchedCategories = data?.categories || [];

        setModels(fetchedDevices);
        setCategories(fetchedCategories);
        setFilteredModels(fetchedDevices);
      } catch (err) {
        console.error("Failed to load models and categories:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchModelsAndCategories();
  }, [brandId]);

  // Combined real-time filter for Category + Text Search
  useEffect(() => {
    let result = models;

    // 1. Apply Category Filter
    if (selectedCategoryId !== null) {
      result = result.filter((device) => device.category_id === selectedCategoryId);
    }

    // 2. Apply Text Search Filter
    if (searchQuery.trim() !== "") {
      result = result.filter((device) =>
        device.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredModels(result);
  }, [selectedCategoryId, searchQuery, models]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleClearAllFilters = () => {
    setSearchQuery("");
    setSelectedCategoryId(null);
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-3">
        <Loader2 className="w-10 h-10 text-[#363C90] animate-spin" />
        <p className="text-slate-400 text-sm font-bold uppercase tracking-widest">Loading {brandName} Models...</p>
      </div>
    );
  }

  return (
    <div className="pb-16 bg-[#F8FAFC] min-h-screen">
      
      {/* --- COMPACT HEADER SECTION --- */}
      <div className="bg-white border-b border-slate-100 mb-6">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-6">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 text-slate-400 hover:text-[#363C90] transition-colors mb-4 text-[11px] font-bold uppercase tracking-wider"
          >
            <ArrowLeft size={14} /> Back to Brands
          </button>

          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-black text-slate-900 tracking-tight">
                Sell Your <span className="text-[#363C90]">{brandName}</span>
              </h1>
              <p className="text-slate-400 text-[13px] font-medium mt-0.5">Choose your specific category or model to get an instant quote.</p>
            </div>

            {/* Sharp Search Bar */}
            <div className="relative w-full md:w-[320px]">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <input
                type="text"
                placeholder={`Search ${brandName}...`}
                className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-100 rounded-xl focus:outline-none focus:bg-white focus:border-[#21C2A5]/30 focus:ring-4 focus:ring-[#21C2A5]/5 transition-all text-sm font-bold text-slate-700"
                value={searchQuery}
                onChange={handleSearch}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1200px] mx-auto px-4 md:px-6">
        
        {/* --- PREMIUM DYNAMIC CATEGORY PILLS FILTER --- */}
        {categories.length > 0 && (
          <div className="mb-6">
            <label className="text-slate-400 text-[10px] font-black uppercase tracking-wider block mb-2.5">Filter by Type</label>
            <div className="flex flex-wrap gap-2 items-center">
              <button
                onClick={() => setSelectedCategoryId(null)}
                className={`px-4 py-2 rounded-xl text-xs font-bold tracking-tight transition-all border ${
                  selectedCategoryId === null
                    ? "bg-[#363C90] text-white border-[#363C90] shadow-md shadow-[#363C90]/10"
                    : "bg-white text-slate-600 border-slate-100 hover:border-slate-200"
                }`}
              >
                All Models
              </button>
              
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategoryId(cat.id)}
                  className={`px-4 py-2 rounded-xl text-xs font-bold tracking-tight transition-all border ${
                    selectedCategoryId === cat.id
                      ? "bg-[#363C90] text-white border-[#363C90] shadow-md shadow-[#363C90]/10"
                      : "bg-white text-slate-600 border-slate-100 hover:border-slate-200"
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* --- SHARP PRODUCT GRID --- */}
        {filteredModels.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredModels.map((item) => (
              <div
                key={item.id}
                onClick={() => navigate(`/sell/device/${item.id}?model=${encodeURIComponent(item.name)}`)}
                className="group bg-white border border-slate-100 rounded-2xl p-3 flex items-center gap-4 hover:border-[#21C2A5] hover:shadow-xl hover:shadow-slate-200/50 transition-all cursor-pointer relative overflow-hidden"
              >
                {/* Compact Image Wrapper */}
                <div className="w-16 h-16 bg-slate-50 rounded-xl flex items-center justify-center overflow-hidden p-2 group-hover:bg-white transition-colors">
                  {item.image ? (
                    <img 
                      src={item.image} 
                      alt={item.name} 
                      className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-500" 
                    />
                  ) : (
                    <Smartphone size={24} className="text-slate-200" />
                  )}
                </div>

                {/* Info Section */}
                <div className="flex-1 min-w-0">
                  <h3 className="text-[14px] font-bold text-slate-800 leading-tight group-hover:text-[#363C90] transition-colors truncate">
                    {item.name}
                  </h3>
                  <div className="mt-2 flex items-center text-[9px] font-black text-[#21C2A5] tracking-[1.5px] uppercase">
                    Check Price <ChevronRight size={12} className="ml-1 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
                
                {/* Subtle Hover Line Element */}
                <div className="absolute top-0 right-0 w-1 h-full bg-[#21C2A5] opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            ))}
          </div>
        ) : (
          /* --- CLEAN EMPTY STATE --- */
          <div className="py-20 flex flex-col items-center text-center bg-white rounded-[2rem] border border-slate-100 shadow-sm">
            <div className="w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center mb-4">
              {selectedCategoryId ? <Layers size={32} className="text-slate-200" /> : <Smartphone size={32} className="text-slate-200" />}
            </div>
            <h2 className="text-lg font-bold text-slate-800">No Model Found</h2>
            <p className="text-slate-400 mt-1 text-sm max-w-[320px] font-medium">
              {searchQuery 
                ? `We couldn't find matches for "${searchQuery}" in this context.`
                : `No active models listed under this category for ${brandName}.`}
            </p>
            <button 
              onClick={handleClearAllFilters}
              className="mt-6 px-6 py-2.5 bg-[#363C90] text-white rounded-xl text-[12px] font-bold hover:bg-[#2a2f70] transition-all"
            >
              Reset Filters & Show All
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SelectProduct;